import { StaffUserRole } from "@gymflow/types";
import { validatePhoneNumber } from "@gymflow/validation";
import { z } from "zod";

import { RoleOption } from "./types";

export const roleOptions: RoleOption[] = [
  { label: "Owner", value: "OWNER", id: "owner" },
  { label: "Manager", value: "MANAGER", id: "manager" },
  { label: "Reception", value: "RECEPTION", id: "reception" },
  { label: "Trainer", value: "TRAINER", id: "trainer" },
];

export const placeholderRole = {
  id: "",
  label: "Select a Role",
  value: undefined,
};

export const INITIAL_VALUES_STAFF_FORM: z.infer<
  ReturnType<typeof staffSidebarSchema>
> = {
  email: "",
  firstName: "",
  lastName: "",
  mobileNumber: "",
  picture: null,
  clubAccessibleList: [],
  role: null,
};

const selectOptionSchema = z.object({
  label: z.string(),
  value: z.object({
    id: z.union([z.string(), z.number()]),
    name: z.string(),
  }),
});

export const staffSidebarSchema = (defaultClub?: {
  label?: string;
  value?: { name?: string; id?: number };
}) =>
  z
    .object({
      id: z.string().optional(),
      email: z
        .string({ required_error: "Email is required" })
        .email("Invalid email"),
      firstName: z
        .string({ required_error: "First name is required" })
        .trim()
        .min(2, "First name must be at least 2 characters"),
      lastName: z
        .string({ required_error: "Last name is required" })
        .trim()
        .min(2, "Last name must be at least 2 characters"),
      mobileNumber: z
        .string({ required_error: "Mobile number is required" })
        .refine((e) => validatePhoneNumber(e), {
          message: "validation.phoneNumber",
        }),
      picture: z.string().nullable().optional().default(null),
      clubAccessibleList: z
        .array(selectOptionSchema)
        .min(1, "Must have access to at least one location.")
        .default(
          defaultClub
            ? [
                {
                  label: defaultClub.label ?? "",
                  value: {
                    id: defaultClub.value?.id ?? 0,
                    name: defaultClub.value?.name ?? "",
                  },
                },
              ]
            : [],
        ),
      role: z
        .object({
          id: z.union([z.string(), z.number()]).optional(),
          label: z.string().optional(),
          value: z.custom<StaffUserRole>().optional(),
        })
        .required()
        .nullable()
        .refine((val) => val !== null && val.id !== undefined, {
          message: "Role is required",
        }),
    })
    .superRefine((data, ctx) => {
      if (!data.id && !data.role) {
        return;
      }

      if (
        !data["clubAccessibleList"] ||
        data["clubAccessibleList"].length < 1
      ) {
        ctx.addIssue({
          path: ["clubAccessibleList"],
          code: z.ZodIssueCode.custom,
          message: "Must have access to at least one location.",
        });
      }
    });
